import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { AnimatePresence, motion } from 'framer-motion';
import {
  getElementTourRect,
  getTourCardSx,
  getTourCardInnerSx,
} from './tourPosition';

const ACCENT = '#137274';
const PINK = '#f50057';

/**
 * Reusable first-visit spotlight tour.
 *
 * @param {object} props
 * @param {Array} props.steps
 * @param {string} props.storageKey
 * @param {string} props.startEvent
 * @param {string} [props.completeEvent]
 * @param {boolean} props.active — when false, tour stays closed
 * @param {number} [props.autoStartDelay=900]
 * @param {() => boolean} [props.isReady] — gate auto/manual start until targets exist
 * @param {React.ReactNode} [props.finishIcon]
 */
const SpotlightTour = ({
  steps: stepsProp,
  storageKey,
  startEvent,
  completeEvent,
  active,
  autoStartDelay = 900,
  isReady,
  finishIcon,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [forced, setForced] = useState(false);

  const steps = useMemo(() => stepsProp || [], [stepsProp]);
  const step = steps[stepIndex] || steps[0];
  const progress = steps.length ? ((stepIndex + 1) / steps.length) * 100 : 0;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const isDone = useCallback(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return true;
    }
  }, [storageKey]);

  const markDone = useCallback(() => {
    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      /* ignore */
    }
    if (completeEvent) {
      window.dispatchEvent(new CustomEvent(completeEvent));
    }
  }, [storageKey, completeEvent]);

  const closeTour = useCallback(
    (completed = true) => {
      setOpen(false);
      setForced(false);
      setStepIndex(0);
      setRect(null);
      if (completed) markDone();
    },
    [markDone],
  );

  const measure = useCallback(() => {
    const current = steps[stepIndex];
    if (!current?.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(current.target);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({
      behavior: 'smooth',
      block: isMobile ? 'center' : 'nearest',
      inline: 'nearest',
    });
    setRect(getElementTourRect(current.target));
  }, [stepIndex, steps, isMobile]);

  const openNow = useCallback((force) => {
    setForced(force);
    setStepIndex(0);
    setOpen(true);
  }, []);

  const startTour = useCallback(
    (force = false) => {
      if (!active) return;
      if (!force && isDone()) return;

      if (typeof isReady === 'function' && !isReady()) {
        let tries = 0;
        const id = setInterval(() => {
          tries += 1;
          if (isReady() || tries > 40) {
            clearInterval(id);
            if (isReady() || force) openNow(force);
          }
        }, 250);
        return;
      }

      openNow(force);
    },
    [active, isDone, isReady, openNow],
  );

  useEffect(() => {
    if (!active || isDone()) return undefined;
    const timer = setTimeout(() => startTour(false), autoStartDelay);
    return () => clearTimeout(timer);
  }, [active, autoStartDelay, isDone, startTour]);

  useEffect(() => {
    if (!startEvent) return undefined;
    const onStart = () => startTour(true);
    window.addEventListener(startEvent, onStart);
    return () => window.removeEventListener(startEvent, onStart);
  }, [startEvent, startTour]);

  useEffect(() => {
    if (!open) return undefined;
    const current = steps[stepIndex];
    if (!current?.target) return undefined;
    const timer = setTimeout(() => {
      if (!document.querySelector(current.target) && stepIndex < steps.length - 1) {
        setStepIndex((i) => i + 1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [open, stepIndex, steps]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(measure, 100);
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    const id = setInterval(measure, 500);
    return () => {
      clearTimeout(t);
      clearInterval(id);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, measure, stepIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeTour(true);
      if (e.key === 'ArrowRight') {
        if (stepIndex >= steps.length - 1) closeTour(true);
        else setStepIndex((i) => i + 1);
      }
      if (e.key === 'ArrowLeft') setStepIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, stepIndex, steps.length, closeTour]);

  const goNext = () => {
    if (isLast) {
      closeTour(true);
      return;
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  if (!open || !step || !active) return null;

  const isCentered = !rect || step.placement === 'center';
  const cardSx = getTourCardSx({
    rect,
    placement: step.placement,
    isMobile,
    zIndex: 1502,
  });
  const innerSx = getTourCardInnerSx(isMobile, isCentered);

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1500 }}>
      <Box
        onClick={() => closeTour(true)}
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: isCentered ? alpha('#020808', 0.64) : 'transparent',
        }}
      />

      {rect && !isCentered && (
        <Box
          sx={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: 2.5,
            boxShadow: `0 0 0 9999px ${alpha('#020808', 0.64)}`,
            outline: `2px solid ${alpha('#7ed4d4', 0.95)}`,
            zIndex: 1501,
            pointerEvents: 'none',
            transition:
              'top 0.22s ease, left 0.22s ease, width 0.22s ease, height 0.22s ease',
          }}
        />
      )}

      <Box sx={cardSx} onClick={(e) => e.stopPropagation()}>
        <AnimatePresence mode="wait">
          <Box
            key={step.id}
            component={motion.div}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            sx={innerSx}
          >
            <Box
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${isDark ? alpha('#7ed4d4', 0.25) : alpha(ACCENT, 0.2)}`,
                bgcolor: isDark ? '#121a1c' : '#ffffff',
                boxShadow: isDark
                  ? '0 24px 64px rgba(0,0,0,0.55)'
                  : '0 24px 64px rgba(15,60,70,0.18)',
              }}
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 3,
                  bgcolor: alpha(ACCENT, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: ACCENT },
                }}
              />
              <Box sx={{ p: { xs: 1.75, sm: 2.25 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1,
                      py: 0.4,
                      borderRadius: 99,
                      bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
                      color: isDark ? '#7ed4d4' : ACCENT,
                      maxWidth: '85%',
                    }}
                  >
                    {isLast && finishIcon ? finishIcon : <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                    <Typography
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {step.eyebrow}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => closeTour(true)}
                    aria-label="Skip tour"
                    sx={{ flexShrink: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '1.05rem', sm: '1.15rem' },
                    letterSpacing: '-0.03em',
                    mb: 0.75,
                    lineHeight: 1.25,
                  }}
                >
                  {step.title}
                </Typography>
                <Typography
                  sx={{
                    color: isDark ? '#8b949e' : '#5f6b73',
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    lineHeight: 1.55,
                    mb: 2,
                  }}
                >
                  {step.body}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: isDark ? '#6e7681' : '#8a969c', fontWeight: 700 }}
                  >
                    {stepIndex + 1} / {steps.length}
                    {forced ? ' · replay' : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                    {!isFirst && (
                      <Button
                        size="small"
                        onClick={goBack}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          color: isDark ? '#c9d1d9' : '#334',
                        }}
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      onClick={goNext}
                      endIcon={!isLast ? <ArrowForwardIcon /> : undefined}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        bgcolor: isLast ? PINK : ACCENT,
                        px: 1.75,
                        '&:hover': { bgcolor: isLast ? '#c40048' : '#0f5c5e' },
                      }}
                    >
                      {step.cta || (isLast ? 'Finish' : 'Next')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default SpotlightTour;
